import { action, computed, makeObservable, observable } from 'mobx';
import {
  GroupError,
  GroupNewResource,
  NewingArgs,
  State,
  addParticipants,
  creating,
  formSubmissionError,
  loadError,
  loading,
  newing,
  success,
  waiting,
} from './Types';

import { assertNever } from '@kofno/piper';
import GroupsStore from '../GroupsStore';
import { GroupResource } from '../GroupsStore/Types';
import { FormTemplate, fieldsToTemplate, fieldsToValues } from '../Resource/FormTemplates/Types';

class GroupStore {
  @observable
  _state: State = waiting();

  readonly groupsStore: GroupsStore;

  constructor(groupsStore: GroupsStore) {
    makeObservable(this);
    this.groupsStore = groupsStore;
  }

  @computed
  get state(): State {
    switch (this._state.kind) {
      case 'waiting':
        return this.groupsStore.state.kind === 'ready' ? { kind: 'loading' } : this._state;
      case 'loading':
      case 'newing':
      case 'load-error':
      case 'form-submission-error':
      case 'success':
      case 'add-participants':
      case 'creating':
        return this._state;
      default:
        return assertNever(this._state);
    }
  }

  @action
  waiting = () => {
    this._state = waiting();
  };

  @action
  formSubmissionError = (
    groupNewResource: GroupNewResource,
    error: GroupError,
    template: FormTemplate,
  ) => {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'newing':
      case 'creating':
      case 'success':
      case 'add-participants':
      case 'load-error':
      case 'form-submission-error':
        this._state = formSubmissionError(groupNewResource, error, template);
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  loadError = (error: GroupError) => {
    switch (this.state.kind) {
      case 'loading':
        this._state = loadError(error);
        break;
      case 'creating':
      case 'waiting':
      case 'newing':
      case 'success':
      case 'add-participants':
      case 'load-error':
      case 'form-submission-error':
        break;
      default:
        return assertNever(this.state);
    }
  };

  @action
  newing = (newingArgs: NewingArgs) => {
    switch (this.state.kind) {
      case 'loading':
        this._state = newing(newingArgs);
        break;
      case 'waiting':
      case 'newing':
      case 'creating':
      case 'success':
      case 'add-participants':
      case 'load-error':
      case 'form-submission-error':
        break;
      default:
        return assertNever(this.state);
    }
  };

  @action
  saving = () => {
    switch (this.state.kind) {
      case 'newing':
        const newingState = this.state;
        fieldsToValues(this.state.template.fields.map((field) => ({ ...field, errors: [] })))
          .do((values) => (this._state = creating(newingState, values)))
          .mapError(fieldsToTemplate(this.state.template))
          .elseDo((errTemplate) => {
            this._state = newing({ resource: newingState.groupNewResource, template: errTemplate });
          });
        break;
      case 'loading':
      case 'waiting':
      case 'creating':
      case 'success':
      case 'add-participants':
      case 'load-error':
      case 'form-submission-error':
        break;
      default:
        return assertNever(this.state);
    }
  };

  @action
  success = (group: GroupResource) => {
    switch (this.state.kind) {
      case 'creating':
        this._state = success(group);
        break;
      case 'waiting':
      case 'loading':
      case 'newing':
      case 'success':
      case 'add-participants':
      case 'load-error':
      case 'form-submission-error':
        break;
      default:
        return assertNever(this.state);
    }
  };

  @action
  addParticipants = () => {
    switch (this.state.kind) {
      case 'success':
        this._state = addParticipants(this.state);
        break;
      case 'creating':
      case 'waiting':
      case 'loading':
      case 'newing':
      case 'add-participants':
      case 'load-error':
      case 'form-submission-error':
        break;
      default:
        return assertNever(this.state);
    }
  };

  @action
  loadingForNew = () => {
    switch (this.state.kind) {
      case 'waiting':
      case 'success':
      case 'add-participants':
        this._state = loading();
        break;
      case 'loading':
      case 'newing':
      case 'creating':
      case 'load-error':
      case 'form-submission-error':
        break;
      default:
        return assertNever(this.state);
    }
  };
}

export default GroupStore;
